import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as tripTypesApi from './trip-types.api';

import type {
  CreateTripTypeRequest,
  TripTypeListQuery,
  UpdateTripTypeRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: TripTypeListQuery) => ['trip-types', 'list', query] as const,
  detail: (id: string) => ['trip-types', 'detail', id] as const,
};

export const useTripTypesList = (query: TripTypeListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => tripTypesApi.listTripTypes(query),
    staleTime: 30_000,
  });

export const useCreateTripType = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateTripTypeRequest) => tripTypesApi.createTripType(payload),
    onSuccess: (dept) => {
      qc.invalidateQueries({ queryKey: ['trip-types', 'list'] });
      toast.success(`Added ${dept.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add tripType'),
  });
};

export const useUpdateTripType = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateTripTypeRequest }) =>
      tripTypesApi.updateTripType(id, payload),
    onSuccess: (dept) => {
      qc.invalidateQueries({ queryKey: ['trip-types', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(dept.id) });
      toast.success('TripType updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update tripType'),
  });
};

export const useDeleteTripType = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tripTypesApi.deleteTripType(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['trip-types', 'list'] });
      toast.success('TripType deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete tripType'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
